import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../firebase";
import { collection, onSnapshot, addDoc, deleteDoc, doc, updateDoc, query, where, getDocs } from "firebase/firestore";

function norm(v) {
  return String(v || '').trim();
}

function empresaEstaActiva(empresa) {
  const estado = String(empresa?.estado || '').trim().toLowerCase();
  return estado === 'activa' || estado === 'validada';
}

function serviciosToStrings(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((s) => (typeof s === 'string' ? s : s?.nombre || s?.id || ''))
    .map((s) => norm(s))
    .filter(Boolean);
}

export default function ListadoServiciosAdmin() {
  const [servicios, setServicios] = useState([]);
  const [nuevoServicio, setNuevoServicio] = useState('');
  const [servicioEditando, setServicioEditando] = useState(null);
  const [nombreEditando, setNombreEditando] = useState('');
  const [loading, setLoading] = useState(false);
  const [empresasActivas, setEmpresasActivas] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [expandedServicio, setExpandedServicio] = useState(null);
  const [busquedaServicioActivo, setBusquedaServicioActivo] = useState('');

  // Servicios predefinidos sugeridos
  const serviciosSugeridos = [
    'Mecánica General', 'Cambio de Aceite', 'Alineación y Balanceo', 'Reparación de Frenos',
    'Suspensión', 'Transmisión', 'Motor', 'Electricidad Automotriz', 'Aire Acondicionado',
    'Pintura', 'Chapa y Pintura', 'Lavado y Detallado', 'Vulcanización', 'Reciclaje',
    'Venta de Repuestos', 'Venta de Accesorios', 'Instalación de Alarmas', 'Instalación de Audio',
    'Revisión Técnica', 'Seguros Automotrices', 'Grúas y Remolque', 'Lubricación',
    'Cambio de Neumáticos', 'Reparación de Cristales', 'Tapicería', 'Tunning', 'GNC',
    'Diagnóstico Computarizado', 'Soldadura', 'Enderezado', 'Pulido de Faros'
  ];

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "servicios"), (snap) => {
      const serviciosList = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Ordenar alfabéticamente
      serviciosList.sort((a, b) => String(a?.nombre || '').localeCompare(String(b?.nombre || ''), 'es'));
      setServicios(serviciosList);
    });
    return () => unsub();
  }, []);

  // Empresas activas/validadas para conteo por servicio (solo lectura admin)
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingEmpresas(true);
      try {
        // Solo condición requerida: empresas activas/validadas.
        // Evitamos filtros extra (p.ej. publicStatus) para no depender de índices compuestos.
        const empresasSnap = await getDocs(query(collection(db, 'empresas'), where('estado', 'in', ['activa', 'validada'])));
        const list = empresasSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!cancelled) setEmpresasActivas(list.filter(empresaEstaActiva));
      } catch (e) {
        if (!cancelled) setEmpresasActivas([]);
      } finally {
        if (!cancelled) setLoadingEmpresas(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const serviciosEnEmpresasActivas = useMemo(() => {
    const map = new Map(); // servicioNorm -> { servicio, empresas: [] }
    for (const emp of empresasActivas) {
      const serviciosEmpresa = serviciosToStrings(emp?.servicios);
      for (const s of serviciosEmpresa) {
        const key = s.toLowerCase();
        if (!map.has(key)) map.set(key, { servicio: s, empresas: [] });
        map.get(key).empresas.push({ id: emp.id, nombre: emp.nombre || emp.nombre_empresa || 'Empresa' });
      }
    }
    const list = [...map.values()].map((row) => ({
      servicio: row.servicio,
      count: row.empresas.length,
      empresas: row.empresas.sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es')),
    }));
    list.sort((a, b) => a.servicio.localeCompare(b.servicio, 'es'));
    return list;
  }, [empresasActivas]);

  const serviciosFiltradosPorBusqueda = useMemo(() => {
    const q = norm(busquedaServicioActivo).toLowerCase();
    if (!q) return serviciosEnEmpresasActivas;
    return serviciosEnEmpresasActivas.filter((row) => String(row.servicio || '').toLowerCase().includes(q));
  }, [serviciosEnEmpresasActivas, busquedaServicioActivo]);

  // Verificar si un servicio ya existe (case-insensitive)
  const servicioExiste = async (nombre) => {
    const nombreNormalizado = nombre.trim().toLowerCase();
    return servicios.some(s => s.nombre.toLowerCase() === nombreNormalizado);
  };

  const agregarServicio = async (nombre) => {
    if (!nombre.trim()) return;
    
    // Verificar duplicados
    const existe = await servicioExiste(nombre);
    if (existe) {
      alert('Este servicio ya existe. No se pueden agregar servicios duplicados.');
      return;
    }
    
    setLoading(true);
    try {
      await addDoc(collection(db, 'servicios'), {
        nombre: nombre.trim(),
        fechaCreacion: new Date(),
        activa: true
      });
      setNuevoServicio('');
      console.log('✅ Servicio agregado:', nombre);
    } catch (error) {
      console.error('❌ Error agregando servicio:', error);
      alert('Error al agregar el servicio');
    } finally {
      setLoading(false);
    }
  };

  const editarServicio = async (id, nuevoNombre) => {
    if (!nuevoNombre.trim()) return;
    
    // Verificar duplicados (excepto el actual)
    const nombreNormalizado = nuevoNombre.trim().toLowerCase();
    const existe = servicios.some(s => 
      s.nombre.toLowerCase() === nombreNormalizado && s.id !== id
    );
    
    if (existe) {
      alert('Ya existe un servicio con este nombre. No se pueden tener servicios duplicados.');
      return;
    }
    
    setLoading(true);
    try {
      await updateDoc(doc(db, 'servicios', id), {
        nombre: nuevoNombre.trim(),
        fechaModificacion: new Date()
      });
      setServicioEditando(null);
      setNombreEditando('');
      console.log('✅ Servicio editado:', nuevoNombre);
    } catch (error) {
      console.error('❌ Error editando servicio:', error);
      alert('Error al editar el servicio');
    } finally {
      setLoading(false);
    }
  };

  const eliminarServicio = async (id, nombre) => {
    if (!confirm(`¿Estás seguro de eliminar el servicio "${nombre}"?`)) return;
    
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'servicios', id));
      console.log('✅ Servicio eliminado:', nombre);
    } catch (error) {
      console.error('❌ Error eliminando servicio:', error);
      alert('Error al eliminar el servicio');
    } finally {
      setLoading(false);
    }
  };

  const agregarServiciosSugeridos = async () => {
    if (!confirm('¿Deseas agregar todos los servicios sugeridos que no existen aún?')) return;
    
    setLoading(true);
    const serviciosExistentes = servicios.map(s => s.nombre.toLowerCase());
    const serviciosNuevos = serviciosSugeridos.filter(
      servicio => !serviciosExistentes.includes(servicio.toLowerCase())
    );
    
    try {
      for (const servicio of serviciosNuevos) {
        await addDoc(collection(db, 'servicios'), {
          nombre: servicio,
          fechaCreacion: new Date(),
          activa: true
        });
      }
      console.log(`✅ ${serviciosNuevos.length} servicios agregados`);
      alert(`Se agregaron ${serviciosNuevos.length} servicios nuevos`);
    } catch (error) {
      console.error('❌ Error agregando servicios sugeridos:', error);
      alert('Error al agregar los servicios sugeridos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Gestión de Servicios</h2>
        <p className="text-gray-600">Administra los servicios disponibles en la plataforma. Los servicios no pueden repetirse.</p>
      </div>

      {/* Servicios usados por empresas activas */}
      <div className="mb-8 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-emerald-900">Servicios en empresas activas</h3>
            <p className="text-sm text-emerald-800">
              Solo se muestran servicios que aparecen en empresas con estado <span className="font-semibold">Activa</span> o <span className="font-semibold">Validada</span>.
            </p>
          </div>
          <div className="text-sm text-emerald-900">
            {loadingEmpresas ? 'Cargando empresas…' : `${serviciosFiltradosPorBusqueda.length} servicios detectados`}
          </div>
        </div>

        <div className="mt-3">
          <input
            type="text"
            value={busquedaServicioActivo}
            onChange={(e) => setBusquedaServicioActivo(e.target.value)}
            placeholder="Buscar servicio..."
            className="w-full md:max-w-md px-3 py-2 border border-emerald-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full bg-white border border-emerald-200 rounded-lg">
            <thead className="bg-emerald-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-emerald-900 uppercase tracking-wider border-b border-emerald-200">
                  Servicio
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-emerald-900 uppercase tracking-wider border-b border-emerald-200">
                  Empresas activas
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-emerald-900 uppercase tracking-wider border-b border-emerald-200">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-emerald-100">
              {loadingEmpresas ? (
                <tr>
                  <td colSpan="3" className="px-4 py-6 text-center text-emerald-800">
                    Cargando…
                  </td>
                </tr>
              ) : serviciosFiltradosPorBusqueda.length === 0 ? (
                <tr>
                  <td colSpan="3" className="px-4 py-6 text-center text-emerald-800">
                    No hay coincidencias para la búsqueda de servicios.
                  </td>
                </tr>
              ) : (
                serviciosFiltradosPorBusqueda.map((row) => {
                  const isExpanded = expandedServicio && expandedServicio.toLowerCase() === row.servicio.toLowerCase();
                  return (
                    <React.Fragment key={row.servicio}>
                      <tr className="hover:bg-emerald-50/40">
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">{row.servicio}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {row.count}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => setExpandedServicio(isExpanded ? null : row.servicio)}
                            className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-sm"
                          >
                            {isExpanded ? 'Ocultar empresas' : 'Ver empresas'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded ? (
                        <tr>
                          <td colSpan="3" className="px-4 py-3 bg-emerald-50/60">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {row.empresas.map((e) => (
                                <a
                                  key={e.id}
                                  href={`/admin/editar-empresa/${e.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-2 bg-white border border-emerald-200 rounded hover:bg-emerald-50 text-sm text-gray-800"
                                >
                                  {e.nombre}
                                </a>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{servicios.length}</div>
          <div className="text-sm text-blue-600">Total Servicios</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {servicios.filter(s => s.activa !== false).length}
          </div>
          <div className="text-sm text-green-600">Activos</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-yellow-600">
            {serviciosSugeridos.filter(
              servicio => !servicios.some(s => s.nombre.toLowerCase() === servicio.toLowerCase())
            ).length}
          </div>
          <div className="text-sm text-yellow-600">Sugeridos Pendientes</div>
        </div>
      </div>

      {/* Agregar nuevo servicio */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Agregar Nuevo Servicio</h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={nuevoServicio}
            onChange={(e) => setNuevoServicio(e.target.value)}
            placeholder="Nombre del servicio"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            onKeyPress={(e) => e.key === 'Enter' && agregarServicio(nuevoServicio)}
            disabled={loading}
          />
          <button
            onClick={() => agregarServicio(nuevoServicio)}
            disabled={loading || !nuevoServicio.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            {loading ? 'Agregando...' : 'Agregar'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          ⚠️ El sistema verificará automáticamente que no exista un servicio duplicado
        </p>
      </div>

      {/* Servicios sugeridos */}
      <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Servicios Sugeridos</h3>
          <button
            onClick={agregarServiciosSugeridos}
            disabled={loading}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-gray-400 transition-colors text-sm"
          >
            {loading ? 'Agregando...' : 'Agregar Todas las Sugeridas'}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {serviciosSugeridos.map(servicio => {
            const existe = servicios.some(s => s.nombre.toLowerCase() === servicio.toLowerCase());
            return (
              <span
                key={servicio}
                className={`px-3 py-1 rounded-full text-sm ${
                  existe 
                    ? 'bg-green-100 text-green-800 line-through' 
                    : 'bg-yellow-100 text-yellow-800 cursor-pointer hover:bg-yellow-200'
                }`}
                onClick={!existe ? () => agregarServicio(servicio) : undefined}
                title={existe ? 'Ya existe' : 'Clic para agregar'}
              >
                {servicio} {existe ? '✓' : '+'}
              </span>
            );
          })}
        </div>
      </div>

      {/* Lista de servicios */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Nombre
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Estado
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Fecha Creación
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {servicios.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                  <div className="text-4xl mb-2">🔧</div>
                  <div>No hay servicios registrados</div>
                  <div className="text-sm mt-2">Agrega algunos servicios para comenzar</div>
                </td>
              </tr>
            ) : (
              servicios.map((servicio) => (
                <tr key={servicio.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 border-b">
                    {servicioEditando === servicio.id ? (
                      <input
                        type="text"
                        value={nombreEditando}
                        onChange={(e) => setNombreEditando(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') editarServicio(servicio.id, nombreEditando);
                          if (e.key === 'Escape') {
                            setServicioEditando(null);
                            setNombreEditando('');
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className="font-medium text-gray-900">{servicio.nombre}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 border-b">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      servicio.activa !== false 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {servicio.activa !== false ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-b">
                    {servicio.fechaCreacion ? 
                      new Date(servicio.fechaCreacion.toDate()).toLocaleDateString() : 
                      'No disponible'
                    }
                  </td>
                  <td className="px-4 py-3 text-right border-b">
                    <div className="flex justify-end gap-2">
                      {servicioEditando === servicio.id ? (
                        <>
                          <button
                            onClick={() => editarServicio(servicio.id, nombreEditando)}
                            disabled={loading}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 text-sm"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={() => {
                              setServicioEditando(null);
                              setNombreEditando('');
                            }}
                            className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setServicioEditando(servicio.id);
                              setNombreEditando(servicio.nombre);
                            }}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => eliminarServicio(servicio.id, servicio.nombre)}
                            disabled={loading}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 text-sm"
                          >
                            Eliminar
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span>Procesando...</span>
          </div>
        </div>
      )}
    </div>
  );
}

